<template>
  <b-modal
    :id="'monitored-health-' + resident_id"
    size="md"
    no-close-on-backdrop
    hide-footer
    modal-class="modern-duty-modal"
    @show="fetchHistory"
  >
    <template v-slot:modal-title>
      <div class="monitored-health-modal-title">
        <i class="fas fa-edit monitored-health-modal-title-icon"></i>
        <span>Intercorrência</span>
      </div>
    </template>

    <div class="monitored-health-modal-body">
      <!-- ── Formulário de novo registro ── -->
      <p class="monitored-health-modal-hint">
        Descreva brevemente a situação. Vai ficar sinalizando no dashboard, no
        card do residente na data que lançou a intercorrência.
      </p>

      <div class="mb-2">
        <label>TIPO DE INTERCORRÊNCIA</label>
        <select class="form-control" v-model="type">
          <option value="">SELECIONE...</option>
          <option value="QUEDA">QUEDA</option>
          <option value="INTERNACAO">INTERNAÇÃO</option>
          <option value="INFECCAO">INFECÇÃO</option>
          <option value="ACOMPANHADO_PROBLEMA_SAUDE">
            ACOMPANHADO POR PROBLEMA SAÚDE
          </option>
        </select>
      </div>

      <ChromeSpeechToTextTextarea rows="4" max-rows="10" v-model="text" />

      <ErrorsMessages :errors="errors" class="mt-2" />

      <div class="monitored-health-modal-actions">
        <button
          class="btn-monitored-save"
          @click="save"
          :disabled="loading || !text.trim()"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
          <i v-else class="fas fa-check mr-1"></i>
          Registrar
        </button>
      </div>

      <!-- ── Histórico de registros ── -->
      <div class="mh-history">
        <div class="mh-history-header">
          <i class="fas fa-history mh-history-header-icon"></i>
          <span>Histórico de intercorrências (Hoje)</span>
        </div>

        <!-- Carregando -->
        <div v-if="historyLoading" class="mh-history-loading">
          <i class="fas fa-spinner fa-spin"></i>
          Carregando...
        </div>

        <!-- Sem registros -->
        <div v-show="history.length === 0" class="mh-history-empty border p-2">
          <i class="fas fa-clipboard mh-history-empty-icon"></i>
          <span>Nenhuma intercorrência registrada hoje.</span>
        </div>

        <!-- Lista -->
        <div v-show="history.length > 0" class="mh-history-list">
          <div v-for="item in history" :key="item.id" class="mh-history-item">
            <div class="mh-history-item-meta">
              <span class="mh-history-item-user">
                <i class="fas fa-user-nurse"></i>
                {{ item.user_name }}
              </span>
              <span class="mh-history-item-date">
                {{ formatDate(item.created_at) }}
              </span>
              <ConfirmationDialog
                x_small
                :disabled="loading"
                :loading="loading"
                headerClass="modal-header-red"
                message-title="Excluir?"
                :message-body="`Deseja realmente excluir a intercorrência?`"
                open-class="btn-modern btn-modern-delete"
                open-left-icon="fa-times"
                openText="EXCLUIR"
                @yes="handleDelete(item.id)"
              />
            </div>
            <p class="mh-history-item-text">{{ item.text }}</p>
            <p class="mh-history-item-text">{{ formatType(item.type) }}</p>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ConfirmationDialog from "../Components/ConfirmationDialog";
import ChromeSpeechToTextTextarea from "../Components/ChromeSpeechToTextTextarea";
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";
import { formatDate } from "../../lib/format";

export default {
  name: "ResidentMonitoredHealthModal",

  components: {
    ConfirmationDialog,
    ChromeSpeechToTextTextarea,
    ErrorsMessages,
  },

  props: {
    resident_id: {
      type: [String, Number],
      required: true,
    },
    resident_name: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      type: "",
      text: "",
      loading: false,
      errors: [],
      history: [],
      historyLoading: false,
    };
  },

  methods: {
    formatDate,

    formatType(type) {
      const types = {
        QUEDA: "Queda",
        ACOMPANHADO_PROBLEMA_SAUDE: "Acompanhamento de problema de saúde",
        INTERNACAO: "Internação",
        INFECCAO: "Infecção",
      };
      return types[type] || type;
    },

    // @show do b-modal dispara isso — busca o histórico toda vez que o modal abre
    async fetchHistory() {
      try {
        this.historyLoading = true;

        const response = await api.get(
          "/rest-home/resident/dashboard/monitored-health-problems",
          { params: { resident_id: this.resident_id } }
        );

        const { data, error } = response.data;

        if (!error) {
          this.history = data;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.historyLoading = false;
      }
    },

    async save() {
      try {
        this.loading = true;
        this.errors = [];

        const { resident_id, text, type } = this;

        const response = await api.post(
          "/rest-home/resident/dashboard/monitored-health-problems",
          { resident_id, text, type }
        );

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message || "Intercorrência registrada com sucesso.", "success");
          this.text = "";
          this.type = "";
          // Atualiza a lista sem fechar o modal — operador vê o novo registro na hora
          await this.fetchHistory();
          this.$emit("saved", { resident_id });
          this.$bvModal.hide(`monitored-health-${resident_id}`);
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async handleDelete(id) {
      try {
        this.loading = true;
        this.errors = [];

        const { resident_id } = this;

        const response = await api.delete(
          "/rest-home/resident/dashboard/monitored-health-problems",
          { data: { resident_id, id } }
        );

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message || "Intercorrência excluída com sucesso.", "success");
          this.text = "";
          // Atualiza a lista sem fechar o modal — operador vê o novo registro na hora
          await this.fetchHistory();
          this.$emit("saved", { resident_id });
          this.$bvModal.hide(`monitored-health-${resident_id}`);
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Título ── */
.monitored-health-modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
}

.monitored-health-modal-title-icon {
  color: #d97706;
  font-size: 18px;
}

/* ── Corpo ── */
.monitored-health-modal-body {
  padding: 4px 0 8px;
}

.monitored-health-modal-hint {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
  line-height: 1.5;
}

/* ── Botão salvar ── */
.monitored-health-modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  margin-bottom: 24px;
}

.btn-monitored-save {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  background: #d97706;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.btn-monitored-save:hover:not(:disabled) {
  background: #b45309;
}

.btn-monitored-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Seção histórico ── */
.mh-history {
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
}

.mh-history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 12px;
}

.mh-history-header-icon {
  color: #94a3b8;
  font-size: 13px;
}

/* Carregando */
.mh-history-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #94a3b8;
  padding: 12px 0;
}

/* Vazio */
.mh-history-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #94a3b8;
  padding: 12px 0;
}

.mh-history-empty-icon {
  font-size: 14px;
}

/* Lista com scroll */
.mh-history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 4px;
}

.mh-history-list::-webkit-scrollbar {
  width: 4px;
}

.mh-history-list::-webkit-scrollbar-track {
  background: #f8fafc;
}

.mh-history-list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}

/* Item */
.mh-history-item {
  background: #fafafa;
  border: 1px solid #f1f5f9;
  border-left: 3px solid #fcd34d; /* faixa âmbar — mantém contexto visual do tema */
  border-radius: 6px;
  padding: 10px 12px;
}

.mh-history-item-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 8px;
}

.mh-history-item-user {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.mh-history-item-user i {
  color: #94a3b8;
  font-size: 11px;
}

.mh-history-item-date {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
}

.mh-history-item-text {
  font-size: 13px;
  color: #334155;
  margin: 0;
  line-height: 1.5;
}
</style>